import { useCallback, useMemo, useState } from 'react'
import { usePlayerById } from '../queries'
import type { PlayerListItem, SquadDraft } from '../types'

/**
 * Drives the player stats sheet for a pitch/bench player click.
 *
 * On click the sheet opens immediately from the draft player (no `gwHistory` yet);
 * `usePlayerById` hydrates the full `PlayerListItem` in the background and replaces it.
 * Shared by the squad picker (own team) and the team viewer (other members' teams).
 */
export function usePlayerStatsSheet(draft: SquadDraft) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null)

  // Find the clicked DraftPlayer instantly so the sheet can open without waiting for a fetch.
  const selectedDraftPlayer = useMemo(() => {
    if (!selectedPlayerId) return null
    return (
      draft.starters.find(p => p.playerId === selectedPlayerId) ??
      draft.bench.find(p => p.playerId === selectedPlayerId) ??
      (draft.benchGk?.playerId === selectedPlayerId ? draft.benchGk : null)
    )
  }, [selectedPlayerId, draft.starters, draft.bench, draft.benchGk])

  // Build a minimal PlayerListItem from DraftPlayer for an immediate render.
  // gwHistory is empty until the real fetch resolves.
  const immediate = useMemo<PlayerListItem | null>(() => {
    if (!selectedDraftPlayer) return null
    return {
      playerId:     selectedDraftPlayer.playerId,
      name:         selectedDraftPlayer.name,
      displayName:  selectedDraftPlayer.displayName,
      position:     selectedDraftPlayer.position,
      price:        selectedDraftPlayer.price,
      teamCode:     selectedDraftPlayer.teamCode,
      teamName:     selectedDraftPlayer.teamName,
      teamLogoUrl:  null,
      teamId:       '',
      isEliminated: selectedDraftPlayer.isEliminated,
      isAvailable:  true,
      injuryStatus: 'NONE',
      totalPoints:  0,
      gwPoints:     selectedDraftPlayer.gwPoints,
      avgPoints:    null,
      nextOpponent: null,
      gwHistory:    [],
    }
  }, [selectedDraftPlayer])

  // Full fetch — resolves in background; replaces the minimal version when ready.
  const { data: full } = usePlayerById(selectedPlayerId)

  const playerForSheet = full ?? immediate

  const selectPlayer   = useCallback((playerId: string) => setSelectedPlayerId(playerId), [])
  const clearSelection = useCallback(() => setSelectedPlayerId(null), [])

  return { selectedPlayerId, selectPlayer, clearSelection, playerForSheet }
}
